import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { accountingFormat } from "../../../utils/number/budget-format.ts"
import { WidgetFrame } from "../WidgetFrame"
import { KpiExplanation, emptyStateText } from "../kpi/KpiWidget"
import { getKpiIconEntry } from "../kpi/kpiIconMap"
import {
  type IconTileVariant,
  iconTileTints,
  neutral,
  semantic
} from "../tokens"
import type {
  KpiTile,
  SnapshotAxis,
  SnapshotCell,
  SnapshotPayload,
  WidgetProps
} from "../types"
import { numericValue, overrunWarning } from "./overrun"

interface RowStyle {
  tint: IconTileVariant
  icon: string
}

/** Income reads as gain, cost as spend, profit as the brand measure. */
const ROW_STYLES: Record<string, RowStyle> = {
  income: { tint: "success", icon: "currency" },
  cost: { tint: "warning", icon: "wallet" },
  profit: { tint: "teal", icon: "trending" }
}

/** Home's muted row accents, replacing the widget library's saturated trio. */
/**
 * Three distinct accents so the measures stay tellable apart, all drawn from
 * the brand ramp rather than the status one: a cost row is not a failure and
 * an income row is not a success, so none of these is red or green.
 */
const ROW_INK: Record<string, { bg: string; fg: string }> = {
  income: { bg: "#EDF7F9", fg: "#287587" },
  cost: { bg: "#EFF3F7", fg: "#5A6879" },
  profit: { bg: "#EDF7F9", fg: "#69AEC0" }
}

const DEFAULT_ROW_STYLE: RowStyle = { tint: "teal", icon: "activity" }

// A muted readable red — the snapshot's negative figures should read as a
// number to act on, not an alarm.
const NEGATIVE_INK = "#B23B49"

const InfoIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
)

type Translate = (key: string, defaultValue: string) => string

/**
 * The one-line reading under each figure — deliberately not the tooltip's
 * `description`, which is a paragraph.
 *
 * "Taken in" rather than "invoiced": the to-date column counts lines marked
 * completed, which is not the same event as raising an invoice.
 */
function captionFor(key: string, t: Translate): string {
  switch (key) {
    case "total_expected_income":
      return t(
        "portfolio.financialSnapshot.caption.totalExpectedIncome",
        "Full contract value"
      )
    case "billed_to_date":
      return t(
        "portfolio.financialSnapshot.caption.billedToDate",
        "Taken in so far"
      )
    case "remaining_to_bill":
      return t(
        "portfolio.financialSnapshot.caption.remainingToBill",
        "Left to take in"
      )
    case "budgeted_cost":
      return t(
        "portfolio.financialSnapshot.caption.budgetedCost",
        "Full contract expenses"
      )
    case "cost_to_date":
      return t(
        "portfolio.financialSnapshot.caption.costToDate",
        "Paid out so far"
      )
    case "cost_to_complete":
      return t(
        "portfolio.financialSnapshot.caption.costToComplete",
        "Left to spend"
      )
    case "estimated_gross_profit":
      return t(
        "portfolio.financialSnapshot.caption.estimatedGrossProfit",
        "Expected income minus cost"
      )
    case "gross_profit_to_date":
      return t(
        "portfolio.financialSnapshot.caption.grossProfitToDate",
        "Taken in minus paid out"
      )
    case "remaining_gross_profit":
      return t(
        "portfolio.financialSnapshot.caption.remainingGrossProfit",
        "Profit still to earn"
      )
    default:
      return ""
  }
}

/** The explanation components speak KpiTile; a cell is the same shape minus the layout. */
const asTile = (
  cell: SnapshotCell,
  filtersIgnored?: SnapshotPayload["filters_ignored"]
): KpiTile => ({
  id: cell.key,
  title: cell.title,
  value: cell.value,
  unit: cell.unit,
  drill_to: "",
  has_data: cell.has_data,
  description: cell.description,
  formula: cell.formula,
  basis: cell.basis,
  ...(cell.empty_reason ? { empty_reason: cell.empty_reason } : {}),
  ...(filtersIgnored?.length ? { filters_ignored: filtersIgnored } : {})
})

const formatValue = (cell: SnapshotCell, currency: string): string => {
  const numeric =
    typeof cell.value === "number" ? cell.value : Number(cell.value)
  if (!Number.isFinite(numeric)) return String(cell.value ?? "—")
  return cell.unit === "currency"
    ? accountingFormat(numeric, { currency })
    : String(numeric)
}

/**
 * Nothing planned and nothing spent are both zero, and the client reads a blank
 * value line as a figure the page failed to load. The reason the datum is
 * missing moves to the caption slot rather than standing in for the number.
 */
const withZeroWhenAbsent = (cell: SnapshotCell): SnapshotCell =>
  cell.has_data === false ? { ...cell, value: 0 } : cell

const SnapshotCellBody = ({
  cell,
  currency,
  filtersIgnored
}: {
  cell: SnapshotCell
  currency: string
  filtersIgnored?: SnapshotPayload["filters_ignored"]
}) => {
  const { t } = useTranslation()
  const tile = asTile(cell, filtersIgnored)
  const hasExplanation = Boolean(
    cell.description || cell.formula || filtersIgnored?.length
  )
  const absent = cell.has_data === false
  const caption = captionFor(cell.key, t)
  const emptyText = emptyStateText(tile, t)
  const showEmptyAsValue =
    absent && cell.empty_reason === "nothing_billed"
  const negative = !absent && (numericValue(cell.value) ?? 0) < 0
  const warning = absent ? null : overrunWarning(cell.key, cell.value)

  return (
    <div
      data-testid={`snapshot-cell-${cell.key}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        alignItems: "flex-end",
        padding: 0
      }}
    >
      <span className="sr-only">{cell.title}</span>
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
      >
        {hasExplanation ? (
          <Tooltip
            title={<KpiExplanation tile={tile} />}
            trigger={["hover", "focus"]}
          >
            <button
              type="button"
              data-testid={`snapshot-info-${cell.key}`}
              aria-label={t(
                "portfolio.tile.infoLabel",
                "How this is calculated"
              )}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                lineHeight: 0,
                color: neutral.faint,
                cursor: "help",
                flexShrink: 0
              }}
            >
              <InfoIcon width={12} height={12} />
            </button>
          </Tooltip>
        ) : null}
      <span
        data-testid={`snapshot-value-${cell.key}`}
        style={{
          fontSize: 17,
          fontWeight: 700,
          lineHeight: "22px",
          fontVariantNumeric: "tabular-nums",
          color: absent
            ? neutral.muted
            : negative
              ? NEGATIVE_INK
              : neutral.ink,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {showEmptyAsValue
          ? emptyText
          : formatValue(withZeroWhenAbsent(cell), currency)}
      </span>
      </div>

      {absent && !showEmptyAsValue ? (
        <span
          data-testid={`snapshot-empty-${cell.key}`}
          style={{ fontSize: 12, fontStyle: "italic", color: neutral.faint }}
        >
          {emptyText}
        </span>
      ) : warning ? (
        <span
          data-testid={`snapshot-warning-${cell.key}`}
          role="status"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: semantic.errorText,
            display: "inline-flex",
            alignItems: "center",
            gap: 4
          }}
        >
          <span aria-hidden>⚠</span>
          {t(warning.key, warning.fallback)}
        </span>
      ) : caption ? (
        // Rendered for screen readers only: nine italic glosses under nine
        // figures is what buried the numbers, and the same words are already
        // one hover away in the cell's own explanation tooltip.
        <span data-testid={`snapshot-caption-${cell.key}`} className="sr-only">
          {caption}
        </span>
      ) : null}
    </div>
  )
}

const RowRail = ({ row }: { row: SnapshotAxis }) => {
  const style = ROW_STYLES[row.key] ?? DEFAULT_ROW_STYLE
  const tint = ROW_INK[row.key] ?? iconTileTints[style.tint]
  const { icon: Icon } = getKpiIconEntry({ id: "", icon: style.icon })

  return (
    <div
      data-testid={`snapshot-row-${row.key}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        paddingLeft: 12,
        borderLeft: `3px solid ${tint.fg}`,
        minWidth: 0
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 26,
          height: 26,
          borderRadius: 7,
          background: tint.bg,
          color: tint.fg,
          flexShrink: 0
        }}
      >
        <Icon width={15} height={15} />
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: neutral.ink
        }}
      >
        {row.title}
      </span>
    </div>
  )
}

const SnapshotGrid = ({
  snapshot,
  currency
}: {
  snapshot: SnapshotPayload
  currency: string
}) => {
  const byPosition = new Map<string, SnapshotCell>()
  for (const cell of snapshot.cells)
    byPosition.set(`${cell.row}.${cell.column}`, cell)

  const columns: SnapshotAxis[] = snapshot.columns ?? []
  const rows: SnapshotAxis[] = snapshot.rows ?? []

  // One rule across the whole row rather than a border per cell: the grid's
  // column gap used to break the line into three disconnected underlines.
  const rowDivider = (
    <div
      aria-hidden
      style={{
        gridColumn: "1 / -1",
        height: 1,
        background: neutral.border
      }}
    />
  )

  const columnDivider = (index: number): React.CSSProperties =>
    index === 0 ? {} : { borderLeft: `1px solid ${neutral.border}`, paddingLeft: 24 }

  return (
    // `position: relative` is load-bearing, not decoration: every cell carries
    // an `sr-only` span, which is absolutely positioned. Without a positioned
    // ancestor here their containing block is the card, so they escape this
    // scroll box entirely and sit at the grid's full min-width — enough to put
    // a real horizontal scrollbar on the page's scroller at tablet widths,
    // from 1px of invisible text.
    <div style={{ overflowX: "auto", position: "relative" }}>
      <div
        data-testid="snapshot-grid"
        style={{
          display: "grid",
          // Capped rather than 1fr: stretched columns put hundreds of pixels
          // between a figure and the next one on a wide monitor, which is the
          // opposite of reading a row across.
          gridTemplateColumns: `minmax(104px, 150px) repeat(${columns.length}, minmax(140px, 1fr))`,
          columnGap: 14,
          rowGap: 0,
          alignItems: "center",
          minWidth: 104 + columns.length * 154
        }}
      >
        <span />
        {columns.map((column, columnIndex) => (
          <span
            key={column.key}
            data-testid={`snapshot-column-${column.key}`}
            data-column-index={columnIndex}
            style={{
              fontSize: 12,
              lineHeight: "16px",
              fontWeight: 600,
              color: neutral.muted,
              textAlign: "right",
              padding: "4px 0 10px"
            }}
          >
            {column.title}
          </span>
        ))}

        {rows.map((row, index) => (
          <React.Fragment key={row.key}>
            {index === 0 ? null : rowDivider}
            <div style={{ padding: "10px 0" }}>
              <RowRail row={row} />
            </div>
            {columns.map((column, columnIndex) => {
              const cell = byPosition.get(`${row.key}.${column.key}`)
              return (
                <div
                  key={column.key}
                  style={{
                    ...columnDivider(columnIndex),
                    padding: "6px 0"
                  }}
                >
                  {cell ? (
                    <SnapshotCellBody
                      cell={cell}
                      currency={currency}
                      filtersIgnored={snapshot.filters_ignored}
                    />
                  ) : null}
                </div>
              )
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export const SnapshotWidget = ({
  widget,
  payload,
  state,
  currency = "USD",
  onRefresh,
  onExport,
  editChrome
}: WidgetProps) => {
  const { t } = useTranslation()
  const snapshot = payload?.snapshot
  const hasCells = Boolean(snapshot?.cells?.length)

  return (
    <WidgetFrame
      editChrome={editChrome}
      title={widget.title}
      state={state === "ready" && !hasCells ? "empty" : state}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      onRefresh={onRefresh}
      onExport={onExport}
    >
      {snapshot ? (
        <>
          <div
            data-testid="snapshot-scope"
            style={{ fontSize: 12, color: neutral.muted, marginBottom: 8 }}
          >
            {t(
              "portfolio.financialSnapshot.scope",
              "As of today · not affected by the date filter"
            )}
          </div>
          <SnapshotGrid
            snapshot={snapshot}
            currency={payload?.currency ?? currency}
          />
        </>
      ) : null}
    </WidgetFrame>
  )
}
